import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatMYR } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
    Plus,
    Pencil,
    Trash2,
    Search,
    Package,
    Upload,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    Download,
    RefreshCw,
    X,
    Sparkles,
    ListTree,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import ImageDropUpload from "../components/ImageDropUpload";
import ProductImagesInput from "../components/ProductImagesInput";
import BulkAddProductsDialog from "../components/BulkAddProductsDialog";
import BulkUpdateProductsDialog from "../components/BulkUpdateProductsDialog";
import BulkEyeCategoryDialog from "../components/BulkEyeCategoryDialog";
import ImageLightbox from "../components/ImageLightbox";
import InlineEditNumber from "../components/InlineEditNumber";
import InlineEditText from "../components/InlineEditText";
import InlineEditSelect from "../components/InlineEditSelect";
import { exportProductsCSV, exportProductsTSV } from "../lib/exportCsv";
import { getNextSku } from "../lib/skuUtils";

const OTHER_OPTION = { value: "other", label: "其他 / 非眼数" };

const emptyForm = {
    name: "", sku: "", category: "", description: "", image_url: "", images: [], supplier: "",
    stock: 0, cost_price: 0, unit_price: 0, low_stock_threshold: 0,
    dzi_category: "other", dzi_length_cm: "", featured: false, featured_crystal: false, featured_jade: false,
};

export default function ProductsPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
    const [bulkEyeOpen, setBulkEyeOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [syncingCategories, setSyncingCategories] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editing, setEditing] = useState(null);
    const [sortKey, setSortKey] = useState(null); // 'stock' | 'cost_price' | 'unit_price' | null
    const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'
    const [dziCategories, setDziCategories] = useState([]);
    const [lightbox, setLightbox] = useState(null); // { images, title }
    const [searchParams, setSearchParams] = useSearchParams();
    const [q, setQ] = useState(searchParams.get("q") || "");

    // Sync q ↔ URL so the command palette can deep-link
    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        if (urlQ !== q) setQ(urlQ);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        if (q && q !== urlQ) setSearchParams({ q }, { replace: true });
        else if (!q && urlQ) setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    // Dropdown options = all enabled eye categories from landing-config + 「其他」
    const dziOptions = [
        ...dziCategories.filter((c) => c.enabled).map((c) => ({ value: c.key, label: c.label })),
        OTHER_OPTION,
    ];

    const toggleSort = (key) => {
        if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
        if (sortDir === "asc") { setSortDir("desc"); return; }
        // Third click clears sort → back to default order
        setSortKey(null); setSortDir("asc");
    };

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/products");
        setProducts(data);
        setLoading(false);
        setSelectedIds(new Set());
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async ({ silent = true } = {}) => {
        if (!silent) setSyncingCategories(true);
        try {
            const { data } = await api.get("/admin/landing-config");
            setDziCategories(Array.isArray(data.categories) ? data.categories : []);
            if (!silent) toast.success("眼数分类已同步");
        } catch {
            if (!silent) toast.error("同步失败");
        } finally {
            if (!silent) setSyncingCategories(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm, sku: getNextSku(products) });
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        const existingImages = Array.isArray(p.images) && p.images.length > 0
            ? p.images
            : (p.image_url ? [p.image_url] : []);
        setForm({
            name: p.name, sku: p.sku, category: p.category || "", description: p.description || "",
            image_url: p.image_url || "", images: existingImages, supplier: p.supplier || "",
            stock: p.stock, cost_price: p.cost_price, unit_price: p.unit_price,
            low_stock_threshold: p.low_stock_threshold || 0,
            dzi_category: p.dzi_category || "other",
            dzi_length_cm: p.dzi_length_cm ?? "",
            featured: !!p.featured,
            featured_crystal: !!p.featured_crystal,
            featured_jade: !!p.featured_jade,
            visible_on_shop: p.visible_on_shop !== false,
        });
        setDialogOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                stock: Number(form.stock),
                cost_price: Number(form.cost_price),
                unit_price: Number(form.unit_price),
                low_stock_threshold: Number(form.low_stock_threshold),
                dzi_length_cm: form.dzi_length_cm === "" || form.dzi_length_cm === null ? null : Number(form.dzi_length_cm),
            };
            if (editing) {
                await api.put(`/products/${editing.id}`, payload);
                toast.success("产品已更新");
            } else {
                await api.post("/products", payload);
                toast.success("产品已添加");
            }
            setDialogOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "保存失败");
        }
    };

    const del = async (p) => {
        if (!window.confirm(`确定删除 ${p.name}？`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            toast.success("产品已删除");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "删除失败");
        }
    };

    // Inline-edit save (partial PATCH). Optimistic UI update then reconcile.
    const patchField = async (p, field, value) => {
        const prev = products;
        setProducts((cur) => cur.map((x) => x.id === p.id ? { ...x, [field]: value } : x));
        try {
            const { data } = await api.patch(`/products/${p.id}`, { [field]: value });
            setProducts((cur) => cur.map((x) => x.id === p.id ? { ...x, ...data } : x));
            toast.success("已更新");
        } catch (err) {
            setProducts(prev);
            toast.error(err.response?.data?.detail || "更新失败");
            throw err;
        }
    };

    const filtered = products.filter((p) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s) || (p.category || "").toLowerCase().includes(s);
    });

    const sortedFiltered = useMemo(() => {
        if (!sortKey) return filtered;
        const arr = [...filtered];
        arr.sort((a, b) => {
            const va = Number(a[sortKey] ?? 0);
            const vb = Number(b[sortKey] ?? 0);
            return sortDir === "asc" ? va - vb : vb - va;
        });
        return arr;
    }, [filtered, sortKey, sortDir]);

    const SortIcon = ({ colKey }) => {
        if (sortKey !== colKey) return <ArrowUpDown className="h-3 w-3 text-slate-300" />;
        return sortDir === "asc"
            ? <ArrowUp className="h-3 w-3 text-gold-600" />
            : <ArrowDown className="h-3 w-3 text-gold-600" />;
    };

    const stockBadge = (p) => {
        if (p.stock <= 0) return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-0">缺货</Badge>;
        if (p.stock <= p.low_stock_threshold) return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">库存偏低</Badge>;
        return <Badge className="bg-gold-100 text-gold-700 hover:bg-gold-100 border-0">充足</Badge>;
    };

    return (
        <div className="min-w-0 space-y-6" data-testid="products-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">库存</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">产品管理</h1>
                    <p className="text-sm text-gold-500 mt-1">添加、编辑与追踪你的所有产品</p>
                </div>
                <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
                    <Button
                        data-testid="sync-dzi-btn"
                        variant="outline"
                        onClick={() => loadCategories({ silent: false })}
                        disabled={syncingCategories}
                        className="gap-2"
                        title="从「网站设置」拉最新的天珠分类"
                    >
                        <RefreshCw className={`h-4 w-4 ${syncingCategories ? "animate-spin" : ""}`} /> 同步眼数
                    </Button>
                    <Button
                        data-testid="open-bulk-eye-category-button"
                        variant="outline"
                        onClick={() => setBulkEyeOpen(true)}
                        className="gap-2 border-gold-300 text-gold-700 hover:bg-gold-50"
                    >
                        <ListTree className="h-4 w-4" /> 整理眼数
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button data-testid="export-products-btn" variant="outline" className="gap-2">
                                <Download className="h-4 w-4" /> 导出
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem data-testid="export-csv" onClick={() => exportProductsCSV(sortedFiltered)}>
                                CSV (通用 · Excel 兼容)
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="export-xls" onClick={() => exportProductsTSV(sortedFiltered)}>
                                Excel (.xls)
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                        data-testid="bulk-add-btn"
                        variant="outline"
                        onClick={() => setBulkOpen(true)}
                        className="gap-2"
                    >
                        <Upload className="h-4 w-4" /> 批量添加
                    </Button>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-product-btn" onClick={openCreate} className="bg-gold-600 hover:bg-gold-700 text-white gap-2">
                            <Plus className="h-4 w-4" /> 添加产品
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editing ? "编辑产品" : "添加产品"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="grid grid-cols-2 gap-4 pt-2">
                            <div className="col-span-2 space-y-1.5">
                                <Label>产品名称 *</Label>
                                <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            </div>
                            <div className="space-y-1.5">
                                <Label>SKU 编码 *</Label>
                                <Input data-testid="product-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required className="font-mono" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>分类</Label>
                                <Input data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="如：项链、耳环" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>供应商</Label>
                                <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>眼数分类</Label>
                                <Select value={form.dzi_category || "other"} onValueChange={(v) => setForm({ ...form, dzi_category: v })}>
                                    <SelectTrigger data-testid="product-dzi-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {dziOptions.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>天珠长度（cm）</Label>
                                <Input
                                    data-testid="product-dzi-length-input"
                                    type="number"
                                    step="0.1"
                                    min="1.0"
                                    max="8.0"
                                    value={form.dzi_length_cm ?? ""}
                                    onChange={(e) => setForm({ ...form, dzi_length_cm: e.target.value })}
                                    placeholder="3.8"
                                />
                                <p className="text-xs text-slate-500">供落地页手链设计器计算珠位，留空则按 3.8cm 处理</p>
                            </div>
                            <div className="space-y-1.5">
                                <Label>在落地页显示</Label>
                                <div className="h-10 flex items-center gap-3 px-3 rounded-md border border-slate-200 bg-gold-50/40">
                                    <Switch
                                        data-testid="product-visible-switch"
                                        checked={form.visible_on_shop !== false}
                                        onCheckedChange={(v) => setForm({ ...form, visible_on_shop: v })}
                                    />
                                    <span className="text-sm text-slate-700">
                                        {form.visible_on_shop !== false ? "上架显示" : "隐藏（不上架）"}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>加入「精选天珠」</Label>
                                <div className="h-10 flex items-center gap-3 px-3 rounded-md border border-slate-200 bg-gold-50/40">
                                    <Switch
                                        data-testid="product-featured-switch"
                                        checked={!!form.featured}
                                        onCheckedChange={(v) => setForm({ ...form, featured: v })}
                                    />
                                    <span className="text-sm text-slate-700">
                                        {form.featured ? "已加入精选天珠" : "未加入精选天珠"}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>加入「精选水晶」</Label>
                                <div className="h-10 flex items-center gap-3 px-3 rounded-md border border-slate-200 bg-gold-50/40">
                                    <Switch
                                        data-testid="product-featured-crystal-switch"
                                        checked={!!form.featured_crystal}
                                        onCheckedChange={(v) => setForm({ ...form, featured_crystal: v })}
                                    />
                                    <span className="text-sm text-slate-700">
                                        {form.featured_crystal ? "已加入精选水晶" : "未加入精选水晶"}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>加入「精选翡翠」</Label>
                                <div className="h-10 flex items-center gap-3 px-3 rounded-md border border-slate-200 bg-gold-50/40">
                                    <Switch
                                        data-testid="product-featured-jade-switch"
                                        checked={!!form.featured_jade}
                                        onCheckedChange={(v) => setForm({ ...form, featured_jade: v })}
                                    />
                                    <span className="text-sm text-slate-700">
                                        {form.featured_jade ? "已加入精选翡翠" : "未加入精选翡翠"}
                                    </span>
                                </div>
                            </div>
                            <div className="col-span-2 space-y-1.5">
                                <Label>产品图片（最多 6 张）</Label>
                                <ProductImagesInput
                                    value={form.images}
                                    onChange={(imgs) => setForm({ ...form, images: imgs, image_url: imgs[0] || "" })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>库存数量 *</Label>
                                <Input data-testid="product-stock-input" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} required className="font-mono" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>低库存阈值</Label>
                                <Input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="font-mono" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>成本价 (RM)</Label>
                                <Input type="number" step="0.01" min="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} className="font-mono" />
                                {editing && editing.cost_history?.length > 0 && (
                                    <details className="mt-1 text-xs text-gold-500">
                                        <summary className="cursor-pointer hover:text-slate-700 select-none">成本历史 ({editing.cost_history.length} 次变更)</summary>
                                        <div className="mt-2 space-y-1 max-h-32 overflow-y-auto bg-gold-50 border border-slate-200 rounded p-2">
                                            {editing.cost_history.slice().reverse().map((h) => (
                                                <div key={h.changed_at} className="font-mono text-xs flex justify-between gap-2">
                                                    <span className="text-gold-500">{(h.changed_at || "").slice(0, 10)}</span>
                                                    <span>RM {h.from} → <span className="text-gold-700 font-semibold">RM {h.to}</span></span>
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label>售价 (RM) *</Label>
                                <Input data-testid="product-price-input" type="number" step="0.01" min="0" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} required className="font-mono" />
                            </div>
                            <div className="col-span-2 space-y-1.5">
                                <Label>描述</Label>
                                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <DialogFooter className="col-span-2 pt-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
                                <Button type="submit" data-testid="save-product-btn" className="bg-gold-600 hover:bg-gold-700 text-white">
                                    {editing ? "保存修改" : "添加产品"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            <Card className="min-w-0 border-slate-200 shadow-sm">
                <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
                    <div className="relative min-w-0 flex-1 max-w-sm">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="product-search-input"
                            placeholder="搜索产品名称、SKU、分类..."
                            className="pl-9"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <div className="ml-auto shrink-0 font-mono text-xs text-gold-500">
                        共 {sortedFiltered.length} 项
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-gold-500">加载中...</div>
                ) : sortedFiltered.length === 0 ? (
                    <div className="p-12 text-center">
                        <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <div className="text-sm text-gold-500">{products.length === 0 ? "还没有产品，点击右上角添加" : "没有匹配的产品"}</div>
                    </div>
                ) : (
                    <div className="max-w-full overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-gold-50/60">
                                    <th className="w-10 py-3 pl-4 pr-2">
                                        <Checkbox
                                            data-testid="select-all-checkbox"
                                            checked={sortedFiltered.length > 0 && sortedFiltered.every((p) => selectedIds.has(p.id))}
                                            onCheckedChange={(v) => {
                                                if (v) setSelectedIds(new Set(sortedFiltered.map((p) => p.id)));
                                                else setSelectedIds(new Set());
                                            }}
                                            className="data-[state=checked]:bg-gold-600 data-[state=checked]:border-gold-600"
                                        />
                                    </th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">产品</th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">SKU</th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">分类</th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">眼数分类</th>
                                    <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">落地页</th>
                                    <th
                                        data-testid="sort-stock"
                                        onClick={() => toggleSort("stock")}
                                        className={`text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider select-none cursor-pointer hover:bg-slate-100 transition-colors ${sortKey === "stock" ? "text-gold-700" : "text-gold-500"}`}
                                    >
                                        <span className="inline-flex items-center gap-1 justify-end">库存 <SortIcon colKey="stock" /></span>
                                    </th>
                                    <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">状态</th>
                                    <th
                                        data-testid="sort-cost"
                                        onClick={() => toggleSort("cost_price")}
                                        className={`text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider select-none cursor-pointer hover:bg-slate-100 transition-colors ${sortKey === "cost_price" ? "text-gold-700" : "text-gold-500"}`}
                                    >
                                        <span className="inline-flex items-center gap-1 justify-end">成本 <SortIcon colKey="cost_price" /></span>
                                    </th>
                                    <th
                                        data-testid="sort-price"
                                        onClick={() => toggleSort("unit_price")}
                                        className={`text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider select-none cursor-pointer hover:bg-slate-100 transition-colors ${sortKey === "unit_price" ? "text-gold-700" : "text-gold-500"}`}
                                    >
                                        <span className="inline-flex items-center gap-1 justify-end">售价 <SortIcon colKey="unit_price" /></span>
                                    </th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedFiltered.map((p) => (
                                    <tr key={p.id} data-testid={`product-row-${p.id}`} className={`border-b border-slate-100 hover:bg-gold-50 transition-colors duration-150 ${selectedIds.has(p.id) ? "bg-gold-50/70" : ""}`}>
                                        <td className="py-3 pl-4 pr-2">
                                            <Checkbox
                                                data-testid={`select-${p.id}`}
                                                checked={selectedIds.has(p.id)}
                                                onCheckedChange={(v) => {
                                                    setSelectedIds((cur) => {
                                                        const next = new Set(cur);
                                                        if (v) next.add(p.id); else next.delete(p.id);
                                                        return next;
                                                    });
                                                }}
                                                className="data-[state=checked]:bg-gold-600 data-[state=checked]:border-gold-600"
                                            />
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                {p.image_url ? (
                                                    <button
                                                        type="button"
                                                        data-testid={`product-thumb-${p.id}`}
                                                        onClick={() => setLightbox({
                                                            images: Array.isArray(p.images) && p.images.length > 0 ? p.images : [p.image_url],
                                                            title: p.name,
                                                        })}
                                                        title="点击查看大图"
                                                        className="relative h-9 w-9 shrink-0 rounded overflow-hidden border border-slate-200 hover:ring-2 hover:ring-gold-400 transition-all cursor-zoom-in"
                                                    >
                                                        <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                                                        {Array.isArray(p.images) && p.images.length > 1 && (
                                                            <span className="absolute bottom-0 right-0 px-1 bg-slate-900/70 text-white text-[9px] font-bold leading-tight">
                                                                {p.images.length}
                                                            </span>
                                                        )}
                                                    </button>
                                                ) : (
                                                    <div className="h-9 w-9 rounded bg-slate-100 flex items-center justify-center">
                                                        <Package className="h-4 w-4 text-slate-400" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="text-sm font-medium text-slate-900">{p.name}</div>
                                                    {p.supplier && <div className="text-xs text-gold-500">{p.supplier}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-slate-700 font-mono">{p.sku}</td>
                                        <td className="py-3 px-4 text-sm text-slate-600">
                                            <InlineEditText
                                                value={p.category || ""}
                                                onSave={(v) => patchField(p, "category", v)}
                                                testId={`inline-category-${p.id}`}
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-sm">
                                            <InlineEditSelect
                                                value={p.dzi_category || "other"}
                                                options={dziOptions}
                                                onSave={(v) => patchField(p, "dzi_category", v)}
                                                testId={`inline-dzi-${p.id}`}
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <button
                                                type="button"
                                                data-testid={`inline-visible-${p.id}`}
                                                onClick={(e) => { e.stopPropagation(); patchField(p, "visible_on_shop", p.visible_on_shop === false); }}
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium hover:opacity-80 transition ${
                                                    p.visible_on_shop === false
                                                        ? "bg-slate-100 text-slate-500 border-slate-300"
                                                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                }`}
                                                title={p.visible_on_shop === false ? "点击上架" : "点击隐藏"}
                                            >
                                                {p.visible_on_shop === false ? "已隐藏" : "上架中"}
                                            </button>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right">
                                            <InlineEditNumber
                                                value={p.stock}
                                                onSave={(v) => patchField(p, "stock", v)}
                                                min={0}
                                                step="1"
                                                testId={`inline-stock-${p.id}`}
                                                className="font-semibold text-slate-900"
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-center">{stockBadge(p)}</td>
                                        <td className="py-3 px-4 text-sm text-right">
                                            <InlineEditNumber
                                                value={p.cost_price}
                                                onSave={(v) => patchField(p, "cost_price", v)}
                                                min={0}
                                                step="0.01"
                                                format={formatMYR}
                                                testId={`inline-cost-${p.id}`}
                                                className="text-slate-600"
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right">
                                            <InlineEditNumber
                                                value={p.unit_price}
                                                onSave={(v) => patchField(p, "unit_price", v)}
                                                min={0}
                                                step="0.01"
                                                format={formatMYR}
                                                testId={`inline-price-${p.id}`}
                                                className="text-slate-900 font-semibold"
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button data-testid={`edit-product-${p.id}`} variant="ghost" size="sm" onClick={() => openEdit(p)}>
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button data-testid={`delete-product-${p.id}`} variant="ghost" size="sm" onClick={() => del(p)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
            <BulkAddProductsDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} products={products} />
            <BulkEyeCategoryDialog
                open={bulkEyeOpen}
                onOpenChange={setBulkEyeOpen}
                products={products}
                dziOptions={dziOptions}
                onDone={load}
            />
            <BulkUpdateProductsDialog
                open={bulkUpdateOpen}
                onOpenChange={setBulkUpdateOpen}
                productIds={[...selectedIds]}
                dziOptions={dziOptions}
                onDone={load}
            />

            {/* Selection toolbar — floats at the bottom when items are selected */}
            {selectedIds.size > 0 && (
                <div
                    data-testid="selection-toolbar"
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-xl bg-slate-900 text-white border border-slate-800"
                >
                    <span className="text-sm">
                        已选 <span data-testid="selection-count" className="font-mono font-bold text-gold-300">{selectedIds.size}</span> 件
                    </span>
                    <div className="h-4 w-px bg-slate-700 mx-1" />
                    <Button
                        data-testid="open-bulk-update-btn"
                        onClick={() => setBulkUpdateOpen(true)}
                        size="sm"
                        className="bg-gold-600 hover:bg-gold-700 text-white gap-1.5 h-8"
                    >
                        <Sparkles className="h-3.5 w-3.5" /> 批量修改
                    </Button>
                    <Button
                        data-testid="clear-selection-btn"
                        onClick={() => setSelectedIds(new Set())}
                        size="sm"
                        variant="ghost"
                        className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 gap-1"
                    >
                        <X className="h-3.5 w-3.5" /> 取消
                    </Button>
                </div>
            )}

            <ImageLightbox
                open={!!lightbox}
                images={lightbox?.images || []}
                title={lightbox?.title || ""}
                onClose={() => setLightbox(null)}
            />
        </div>
    );
}
